import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Label } from "../components/ui/label";
import { generateReport, exportReport } from "../api/axios";
import { timeRangeToDates, getTimeRangeLabel, downloadBlob, generatePdfFromReportData } from "../lib/reportUtils";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Download, File } from "lucide-react";

export default function Reports() {
  const [reportType, setReportType] = useState("availability");
  const [timeRange, setTimeRange] = useState<"last24hours" | "last7days" | "last30days" | "last90days" | "lastYear" | "custom">("last7days");
  const [groupBy, setGroupBy] = useState("device");
  const [format, setFormat] = useState<"html" | "pdf" | "csv">("html");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  // Generate report data from backend
  const generateReportMutation = useMutation({
    mutationFn: async () => {
      const { from, to } = timeRangeToDates(timeRange);
      return generateReport({
        reportType: reportType === "incidents" ? "incident" : 
                   reportType === "capacity" ? "capacity-planning" :
                   reportType === "sla" ? "sla-compliance" :
                   reportType,
        period: { from, to },
        title: `${reportType} report`,
        save: true,
      });
    },
  });

  // Get report data
  const reportData = generateReportMutation.data;
  const isLoadingReport = generateReportMutation.isPending;

  // Use only real data from backend - no fallback
  const displayReportData = reportData ? {
    title: reportData.title || `${reportType} report`,
    summary: reportData.summary || {},
    data: reportData.core || [],
    period: reportData.period || timeRangeToDates(timeRange),
    metadata: reportData.metadata,
  } : null;

  // Generate and export report
  const handleGenerateReport = async () => {
    setIsGenerating(true);
    try {
      await generateReportMutation.mutateAsync();
    } catch (error: any) {
      console.error("Failed to generate report:", error);
      alert(`Failed to generate report: ${error.message || "Unknown error"}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Export report (CSV, HTML, or PDF)
  const handleExportReport = async (exportFormat: "CSV" | "HTML" | "PDF") => {
    setIsExporting(true);
    try {
      const { from, to } = timeRangeToDates(timeRange);
      const reportTypeForBackend = reportType === "incidents" ? "incident" : 
                                   reportType === "capacity" ? "capacity-planning" :
                                   reportType === "sla" ? "sla-compliance" :
                                   reportType;

      if (exportFormat === "PDF") {
        // Generate report data first
        const reportResult = await generateReport({
            reportType: reportTypeForBackend,
            period: { from, to },
            title: `${reportType} report`,
          save: false,
        });
        
        // Generate PDF directly from report data
        await generatePdfFromReportData({
          title: reportResult.title || `${reportType} report`,
          summary: reportResult.summary || {},
          data: reportResult.core || [],
          period: reportResult.period || { from, to },
          metadata: reportResult.metadata,
          reportType: reportType,
        }, `${reportType}-report-${new Date().toISOString().split('T')[0]}.pdf`);
      } else {
        // For CSV or HTML, download directly
        const result = await exportReport({
          generatePayload: {
            reportType: reportTypeForBackend,
            period: { from, to },
            title: `${reportType} report`,
          },
          format: exportFormat,
        });
        
        // Ensure blob is properly created
        if (result.blob) {
        downloadBlob(result.blob, result.filename);
        } else {
          throw new Error("Failed to create file blob");
        }
      }
    } catch (error: any) {
      console.error("Failed to export report:", error);
      alert(`Failed to export report: ${error.message || "Unknown error"}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Generate PDF report
  const handleGeneratePDF = () => {
    handleExportReport("PDF");
  };

  // Generate CSV report
  const handleGenerateCSV = () => {
    handleExportReport("CSV");
  };

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-2xl font-semibold text-neutral-dark">Reports</h1>
            <p className="mt-1 text-sm text-gray-500">
              Generate and view system reports and analytics
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex flex-row space-x-2">
            <Button 
              variant="outline" 
              onClick={handleGenerateCSV}
              disabled={isExporting}
            >
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? "Exporting..." : "Export CSV"}
            </Button>
            <Button 
              onClick={handleGeneratePDF}
              disabled={isExporting}
            >
              <File className="h-4 w-4 mr-2" />
              {isExporting ? "Generating..." : "Generate PDF"}
            </Button>
          </div>
        </div>
      </div>

      {/* Report Configuration */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <CardTitle>Report Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="report-type" className="block text-sm font-medium text-gray-700 mb-1">
                Report Type
              </Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger id="report-type">
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="availability">Availability Report</SelectItem>
                  <SelectItem value="performance">Performance Report</SelectItem>
                  <SelectItem value="incidents">Incident Report</SelectItem>
                  <SelectItem value="capacity">Capacity Planning</SelectItem>
                  <SelectItem value="sla">SLA Compliance</SelectItem>
                  <SelectItem value="custom">Custom Report</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="time-range" className="block text-sm font-medium text-gray-700 mb-1">
                Time Range
              </Label>
              <Select value={timeRange} onValueChange={(value) => setTimeRange(value as typeof timeRange)}>
                <SelectTrigger id="time-range">
                  <SelectValue placeholder="Select time range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="last24hours">Last 24 Hours</SelectItem>
                  <SelectItem value="last7days">Last 7 Days</SelectItem>
                  <SelectItem value="last30days">Last 30 Days</SelectItem>
                  <SelectItem value="last90days">Last 90 Days</SelectItem>
                  <SelectItem value="lastYear">Last Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="group-by" className="block text-sm font-medium text-gray-700 mb-1">
                Group By
              </Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger id="group-by">
                  <SelectValue placeholder="Group by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="device">Device</SelectItem>
                  <SelectItem value="deviceType">Device Type</SelectItem>
                  <SelectItem value="group">Device Group</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                  <SelectItem value="none">No Grouping</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="format" className="block text-sm font-medium text-gray-700 mb-1">
                Format
              </Label>
              <Select value={format} onValueChange={(value) => setFormat(value as typeof format)}>
                <SelectTrigger id="format">
                  <SelectValue placeholder="Report format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="html">HTML</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Tabs */}
      <Tabs defaultValue="preview" className="mb-6">
        <TabsList>
          <TabsTrigger value="preview">Preview</TabsTrigger>
          <TabsTrigger value="table">Table View</TabsTrigger>
          <TabsTrigger value="chart">Chart View</TabsTrigger>
        </TabsList>
        
        <TabsContent value="preview" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="border-b pb-4">
                  <h2 className="text-xl font-bold">{displayReportData?.title || `${reportType} report`}</h2>
                  {displayReportData?.summary && Object.keys(displayReportData.summary).length > 0 && (
                    <p className="text-gray-500 text-sm mt-1">
                      {typeof displayReportData.summary === 'string' 
                        ? displayReportData.summary 
                        : 'Report summary available'}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      {getTimeRangeLabel(timeRange)}
                    </span>
                    <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      Generated: {reportData?.metadata?.generatedAt ? new Date(reportData.metadata.generatedAt).toLocaleString() : new Date().toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-4">
                    <Button 
                      onClick={handleGenerateReport} 
                      disabled={isGenerating}
                      variant="outline"
                    >
                      {isGenerating ? "Generating..." : "Generate Report"}
                    </Button>
                  </div>
                </div>
                
                {/* Loading state */}
                {isLoadingReport && (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Generating report...</p>
                  </div>
                )}

                {/* Report content based on type */}
                {!isLoadingReport && !displayReportData && (
                  <div className="text-center py-12">
                    <p className="text-gray-500 mb-4">No report data available. Click "Generate Report" to create a report.</p>
                    <Button onClick={handleGenerateReport} variant="outline">
                      Generate Report
                    </Button>
                  </div>
                )}

                {!isLoadingReport && displayReportData && reportType === "availability" && (
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium">System Availability Summary</h3>
                      <div className="text-3xl font-bold text-secondary">
                        {displayReportData.summary?.overallSystemAvailabilityPercentage 
                          ? `${displayReportData.summary.overallSystemAvailabilityPercentage.toFixed(2)}%`
                          : 'N/A'}
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Device Name
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Device Type
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Group
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Availability
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Uptime
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Downtime
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {displayReportData.data.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                                No availability data available for the selected period.
                              </td>
                            </tr>
                          ) : (
                            displayReportData.data.map((item: any, index: number) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.name || item.deviceName || "Unknown"}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.type || item.deviceType || "N/A"}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.group || item.deviceGroup || "N/A"}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <span className={`${
                                  (item.value || item.availabilityPercentage || 0) >= 99 ? 'text-secondary' : 
                                  (item.value || item.availabilityPercentage || 0) >= 95 ? 'text-warning' : 
                                  'text-danger'
                                }`}>
                                  {(item.value || item.availabilityPercentage || 0).toFixed(2)}%
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.uptimeHours ? item.uptimeHours.toFixed(2) : Math.floor((item.value || item.availabilityPercentage || 0) * 24 * 7 / 100)} hours
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.downtimeHours ? item.downtimeHours.toFixed(2) : Math.floor((100 - (item.value || item.availabilityPercentage || 0)) * 24 * 7 / 100)} hours
                              </td>
                            </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {!isLoadingReport && displayReportData && reportType === "performance" && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Avg CPU Utilization</h3>
                        <div className="text-2xl font-bold">
                          {displayReportData.summary?.averageCpuUtilizationAcrossAllDevices?.toFixed(1) || "N/A"}%
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Avg Memory Usage</h3>
                        <div className="text-2xl font-bold">
                          {displayReportData.summary?.averageMemoryUsageAcrossAllDevices?.toFixed(1) || "N/A"}%
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Peak Performance</h3>
                        <div className="text-2xl font-bold">
                          {displayReportData.summary?.peakPerformancePercentage?.toFixed(1) || "N/A"}%
                        </div>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Device
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Avg CPU (%)
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Avg Memory (%)
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Avg Network (Mbps)
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {displayReportData.data.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                                No performance data available for the selected period.
                              </td>
                            </tr>
                          ) : (
                            displayReportData.data.map((item: any, index: number) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {item.deviceName || item.name || "Unknown"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.averageCpuUtilizationPct || item.cpu || 0).toFixed(1)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.averageMemoryUsagePct || item.memory || 0).toFixed(1)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.averageNetworkThroughputMbps || item.network || 0).toFixed(1)}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  (item.averageCpuUtilizationPct || item.cpu || 0) === 0 ? 'bg-danger bg-opacity-10 text-danger' :
                                  (item.averageCpuUtilizationPct || item.cpu || 0) > 80 ? 'bg-warning bg-opacity-10 text-warning' :
                                  'bg-secondary bg-opacity-10 text-secondary'
                                }`}>
                                  {item.deviceStatus || (item.averageCpuUtilizationPct || item.cpu || 0) === 0 ? 'Offline' :
                                   (item.averageCpuUtilizationPct || item.cpu || 0) > 80 ? 'Warning' :
                                   'Normal'}
                                </span>
                              </td>
                            </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {!isLoadingReport && displayReportData && reportType === "incidents" && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Total Incidents</h3>
                        <div className="text-2xl font-bold">
                          {displayReportData.summary?.totalIncidents || displayReportData.data?.length || 0}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Critical</h3>
                        <div className="text-2xl font-bold text-danger">
                          {displayReportData.summary?.criticalIncidentsCount || 
                           displayReportData.data.filter((item: any) => (item.severity || "").toLowerCase() === "critical").length}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Warning</h3>
                        <div className="text-2xl font-bold text-warning">
                          {displayReportData.summary?.warningIncidentsCount || 
                           displayReportData.data.filter((item: any) => (item.severity || "").toLowerCase() === "warning").length}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Resolved</h3>
                        <div className="text-2xl font-bold text-secondary">
                          {displayReportData.summary?.resolvedIncidentsCount || 
                           displayReportData.data.filter((item: any) => item.resolutionStatus === "Resolved" || item.resolved).length}
                        </div>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Device
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Severity
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Issue
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Time
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Duration
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {displayReportData.data.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                                No incidents data available for the selected period.
                              </td>
                            </tr>
                          ) : (
                            displayReportData.data.map((item: any, index: number) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {item.deviceName || item.device || "Unknown"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  (item.severity || "").toLowerCase() === 'critical' ? 'bg-danger bg-opacity-10 text-danger' :
                                  (item.severity || "").toLowerCase() === 'warning' ? 'bg-warning bg-opacity-10 text-warning' :
                                  'bg-blue-100 text-blue-800'
                                }`}>
                                  {(item.severity || "Info").charAt(0).toUpperCase() + (item.severity || "Info").slice(1)}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.issueDescription || item.message || item.description || "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.timestamp ? new Date(item.timestamp).toLocaleString() : "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.duration || "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.resolutionStatus || (item.resolved ? 'Resolved' : 'Open')}
                              </td>
                            </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {!isLoadingReport && displayReportData && reportType === "capacity" && (
                  <div className="space-y-6">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Device
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Current Utilization
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Projected (3m)
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Threshold
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Time to Threshold
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Action Required
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {displayReportData.data.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                                No capacity planning data available for the selected period.
                              </td>
                            </tr>
                          ) : (
                            displayReportData.data.map((item: any, index: number) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {item.deviceName || item.name || "Unknown"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.currentUtilizationPct || item.currentUtil || 0).toFixed(1)}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.projectedUtilization3mPct || item.projectedUtil || 0).toFixed(1)}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.thresholdPercentage || item.threshold || 80)}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {item.timeToThreshold || "N/A"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  item.actionRequired === 'Immediate' ? 'bg-danger bg-opacity-10 text-danger' :
                                  item.actionRequired === 'Planning' ? 'bg-warning bg-opacity-10 text-warning' :
                                  'bg-secondary bg-opacity-10 text-secondary'
                                }`}>
                                  {item.actionRequired || 'None'}
                                </span>
                              </td>
                            </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    
                    {displayReportData.summary?.capacityPlanningRecommendations && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-sm font-medium mb-2">Capacity Planning Recommendations</h3>
                      <ul className="list-disc pl-5 space-y-1 text-sm">
                          {Array.isArray(displayReportData.summary.capacityPlanningRecommendations) 
                            ? displayReportData.summary.capacityPlanningRecommendations.map((rec: string, idx: number) => (
                                <li key={idx}>{rec}</li>
                              ))
                            : <li>{String(displayReportData.summary.capacityPlanningRecommendations)}</li>
                          }
                      </ul>
                    </div>
                    )}
                  </div>
                )}
                
                {!isLoadingReport && displayReportData && reportType === "sla" && (
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Overall SLA Compliance</h3>
                        <div className="text-2xl font-bold">
                          {displayReportData.summary?.overallSLACompliancePercentage?.toFixed(1) || "N/A"}%
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Services Meeting SLA</h3>
                        <div className="text-2xl font-bold">
                          {displayReportData.summary?.servicesMeetingSLA || 
                           displayReportData.data.filter((item: any) => item.slaStatus === "Met").length}/
                          {displayReportData.data.length}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-500 mb-1">Avg Response Time</h3>
                        <div className="text-2xl font-bold">
                          {displayReportData.summary?.averageResponseTime?.toFixed(2) || "N/A"}s
                        </div>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Service/Device
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Availability %
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Target %
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Response Time
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              SLA Status
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {displayReportData.data.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                                No SLA compliance data available for the selected period.
                              </td>
                            </tr>
                          ) : (
                            displayReportData.data.map((item: any, index: number) => (
                            <tr key={index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {item.serviceOrDeviceName || item.name || "Unknown"}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.availabilityPercentage || item.availability || 0).toFixed(2)}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.targetAvailabilityPercentage || item.target || 99.9).toFixed(2)}%
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {(item.responseTimeSeconds || item.responseTime || 0).toFixed(2)}s
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  (item.slaStatus || item.compliance) === 'Met' ? 'bg-secondary bg-opacity-10 text-secondary' : 
                                  'bg-danger bg-opacity-10 text-danger'
                                }`}>
                                  {item.slaStatus || item.compliance || "Not Met"}
                                </span>
                              </td>
                            </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="table" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                {reportType === "availability" && (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Device Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Device Type
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Group
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Availability
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Uptime
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Downtime
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayReportData && displayReportData.data.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                            No availability data available.
                          </td>
                        </tr>
                      ) : displayReportData ? (
                        displayReportData.data.map((item: any, index: number) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {item.deviceName || item.name || "Unknown"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.deviceType || item.type || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.deviceGroup || item.group || "N/A"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <span className={`${
                              (item.availabilityPercentage || item.value || 0) >= 99 ? 'text-secondary' : 
                              (item.availabilityPercentage || item.value || 0) >= 95 ? 'text-warning' : 
                              'text-danger'
                            }`}>
                              {(item.availabilityPercentage || item.value || 0).toFixed(2)}%
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(item.uptimeHours || 0).toFixed(2)} hours
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(item.downtimeHours || 0).toFixed(2)} hours
                          </td>
                        </tr>
                        ))
                      ) : null}
                    </tbody>
                  </table>
                )}
                
                {displayReportData && reportType === "performance" && (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg CPU (%)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Memory (%)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Network (Mbps)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayReportData.data.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                            No performance data available.
                          </td>
                        </tr>
                      ) : (
                        displayReportData.data.map((item: any, index: number) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{item.deviceName || "Unknown"}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{(item.averageCpuUtilizationPct || 0).toFixed(1)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{(item.averageMemoryUsagePct || 0).toFixed(1)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{(item.averageNetworkThroughputMbps || 0).toFixed(1)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              (item.averageCpuUtilizationPct || 0) === 0 ? 'bg-danger bg-opacity-10 text-danger' :
                              (item.averageCpuUtilizationPct || 0) > 80 ? 'bg-warning bg-opacity-10 text-warning' :
                              'bg-secondary bg-opacity-10 text-secondary'
                            }`}>
                              {item.deviceStatus || 'Normal'}
                            </span>
                          </td>
                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
                
                {displayReportData && reportType === "incidents" && (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayReportData.data.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                            No incidents data available.
                          </td>
                        </tr>
                      ) : (
                        displayReportData.data.map((item: any, index: number) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{item.deviceName || "Unknown"}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              (item.severity || "").toLowerCase() === 'critical' ? 'bg-danger bg-opacity-10 text-danger' :
                              (item.severity || "").toLowerCase() === 'warning' ? 'bg-warning bg-opacity-10 text-warning' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {(item.severity || "Info").charAt(0).toUpperCase() + (item.severity || "Info").slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm">{item.issueDescription || item.message || "N/A"}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{item.timestamp ? new Date(item.timestamp).toLocaleString() : "N/A"}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{item.resolutionStatus || (item.resolved ? 'Resolved' : 'Open')}</td>
                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
                
                {displayReportData && reportType === "capacity" && (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Util (%)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Projected (3m)</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Threshold</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action Required</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayReportData.data.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                            No capacity planning data available.
                          </td>
                        </tr>
                      ) : (
                        displayReportData.data.map((item: any, index: number) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{item.deviceName || "Unknown"}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{(item.currentUtilizationPct || 0).toFixed(1)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{(item.projectedUtilization3mPct || 0).toFixed(1)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{(item.thresholdPercentage || 80)}%</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              item.actionRequired === 'Immediate' ? 'bg-danger bg-opacity-10 text-danger' :
                              item.actionRequired === 'Planning' ? 'bg-warning bg-opacity-10 text-warning' :
                              'bg-secondary bg-opacity-10 text-secondary'
                            }`}>
                              {item.actionRequired || 'None'}
                            </span>
                          </td>
                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
                
                {displayReportData && reportType === "sla" && (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service/Device</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Availability %</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Target %</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Response Time</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SLA Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {displayReportData.data.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                            No SLA compliance data available.
                          </td>
                        </tr>
                      ) : (
                        displayReportData.data.map((item: any, index: number) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{item.serviceOrDeviceName || "Unknown"}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{(item.availabilityPercentage || 0).toFixed(2)}%</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{(item.targetAvailabilityPercentage || 99.9).toFixed(2)}%</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">{(item.responseTimeSeconds || 0).toFixed(2)}s</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              (item.slaStatus || item.compliance) === 'Met' ? 'bg-secondary bg-opacity-10 text-secondary' : 
                              'bg-danger bg-opacity-10 text-danger'
                            }`}>
                              {item.slaStatus || item.compliance || "Not Met"}
                            </span>
                          </td>
                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="chart" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-6">
                {!displayReportData && (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No report data available. Generate a report to view charts.</p>
                  </div>
                )}
                {displayReportData && reportType === "availability" && (
                  <div>
                    <h3 className="text-lg font-medium mb-4">Availability by Device</h3>
                    <div className="h-96">
                      <div className="flex h-full">
                        <div className="w-16 flex flex-col justify-between text-xs text-gray-500">
                          <div>100%</div>
                          <div>75%</div>
                          <div>50%</div>
                          <div>25%</div>
                          <div>0%</div>
                        </div>
                        <div className="flex-1 flex items-end">
                          {displayReportData.data.length === 0 ? (
                            <div className="w-full text-center text-gray-500 py-8">
                              No data available for chart
                            </div>
                          ) : (
                            displayReportData.data.map((item: any, index: number) => (
                            <div key={index} className="flex-1 mx-1 flex flex-col items-center">
                              <div 
                                className={`w-16 ${
                                  (item.availabilityPercentage || item.value || 0) >= 99 ? 'bg-secondary' : 
                                  (item.availabilityPercentage || item.value || 0) >= 95 ? 'bg-warning' : 
                                  'bg-danger'
                                }`}
                                style={{ height: `${Math.min(100, item.availabilityPercentage || item.value || 0)}%` }}
                              ></div>
                              <div className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left whitespace-nowrap">
                                {item.deviceName || item.name || "Unknown"}
                              </div>
                            </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {displayReportData && reportType === "performance" && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium">Performance Metrics</h3>
                    {displayReportData.data.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No performance data available for chart
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={displayReportData.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="deviceName" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                          <Bar dataKey="averageCpuUtilizationPct" fill="#8884d8" name="CPU %" />
                          <Bar dataKey="averageMemoryUsagePct" fill="#82ca9d" name="Memory %" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                )}
                
                {displayReportData && reportType === "incidents" && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium">Incidents Overview</h3>
                    {displayReportData.data.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No incidents data available for chart
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Critical', value: displayReportData.data.filter((i: any) => (i.severity || "").toLowerCase() === "critical").length },
                                { name: 'Warning', value: displayReportData.data.filter((i: any) => (i.severity || "").toLowerCase() === "warning").length },
                                { name: 'Info', value: displayReportData.data.filter((i: any) => !["critical", "warning"].includes((i.severity || "").toLowerCase())).length },
                              ]}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            <Cell fill="#ef4444" />
                            <Cell fill="#f59e0b" />
                            <Cell fill="#3b82f6" />
                          </Pie>
                              <Tooltip />
                            </PieChart>
                          </ResponsiveContainer>
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={[
                              { name: 'Total', value: displayReportData.data.length },
                              { name: 'Resolved', value: displayReportData.data.filter((i: any) => i.resolutionStatus === "Resolved" || i.resolved).length },
                              { name: 'Open', value: displayReportData.data.filter((i: any) => i.resolutionStatus !== "Resolved" && !i.resolved).length },
                            ]}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="name" />
                              <YAxis />
                              <Tooltip />
                              <Bar dataKey="value" fill="#8884d8" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                  </div>
                )}
                
                {displayReportData && reportType === "capacity" && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium">Capacity Utilization</h3>
                    {displayReportData.data.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No capacity data available for chart
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={displayReportData.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="deviceName" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                          <Bar dataKey="currentUtilizationPct" fill="#8884d8" name="Current %" />
                          <Bar dataKey="projectedUtilization3mPct" fill="#82ca9d" name="Projected (3m) %" />
                          <Bar dataKey="thresholdPercentage" fill="#ffc658" name="Threshold %" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                )}
                
                {displayReportData && reportType === "sla" && (
                  <div className="space-y-6">
                    <h3 className="text-lg font-medium">SLA Compliance</h3>
                    {displayReportData.data.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No SLA data available for chart
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={displayReportData.data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="serviceOrDeviceName" angle={-45} textAnchor="end" height={100} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                          <Bar dataKey="availabilityPercentage" fill="#8884d8" name="Availability %" />
                          <Bar dataKey="targetAvailabilityPercentage" fill="#82ca9d" name="Target %" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}